import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save, Trash2, Pencil, Image as ImageIcon, ImageOff } from "lucide-react";
import { api, fmt, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { PageHeader, Btn, Modal, Field, inp, elapsed } from "@/components/ui-kit";
import OrderPanel from "@/components/OrderPanel";
import { useRealtime, LiveDot } from "@/hooks/useRealtime";

const GRID = 10;
const snap = (v) => Math.round(v / GRID) * GRID;
const colors = { free: "border-emerald-400 bg-emerald-50 text-emerald-800", occupied: "border-rose-400 bg-rose-50 text-rose-800", reserved: "border-violet-400 bg-violet-50 text-violet-800", cleaning: "border-amber-400 bg-amber-50 text-amber-800" };

export default function FloorPlan() {
  const { restaurant, reloadRestaurants } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const rid = restaurant.id;
  const [tables, setTables] = useState([]);
  const [edit, setEdit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [orderTable, setOrderTable] = useState(null);
  const canvas = useRef(null);
  const drag = useRef(null);
  const [zones, setZones] = useState(restaurant.zones?.length ? restaurant.zones : ["Sala"]);
  const [zone, setZone] = useState(zones[0]);
  const zoneTables = tables.filter((x) => (x.zone || "Sala") === zone);
  const addZone = async () => {
    if (zones.length >= 10) { toast.error(t("max_zones")); return; }
    const name = window.prompt(t("new_zone_prompt"));
    if (!name?.trim()) return;
    try { const { data } = await api.put(`/restaurants/${rid}/zones`, { zones: [...zones, name.trim()] }); setZones(data.zones); setZone(name.trim()); reloadRestaurants(); } catch (ex) { toast.error(errMsg(ex)); }
  };
  const bgId = restaurant.zone_backgrounds?.[zone];
  const [bgUrl, setBgUrl] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => {
    if (!bgId) { setBgUrl(null); return undefined; }
    let url;
    api.get(`/files/${bgId}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setBgUrl(url); }).catch(() => setBgUrl(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [bgId]);
  const uploadBg = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/restaurants/${rid}/zones/${encodeURIComponent(zone)}/background`, fd, { headers: { "Content-Type": "multipart/form-data" } }); await reloadRestaurants(); toast.success(t("bg_uploaded")); } catch (ex) { toast.error(errMsg(ex)); }
  };
  const removeBg = async () => { try { await api.delete(`/restaurants/${rid}/zones/${encodeURIComponent(zone)}/background`); await reloadRestaurants(); } catch (ex) { toast.error(errMsg(ex)); } };
  const renameZone = async (z) => {
    const name = window.prompt(t("rename_zone_prompt"), z);
    if (!name?.trim() || name.trim() === z) return;
    try { const { data } = await api.put(`/restaurants/${rid}/zones/rename`, { old: z, new: name.trim() }); setZones(data.zones); setZone(name.trim()); reloadRestaurants(); load(); } catch (ex) { toast.error(errMsg(ex)); }
  };
  const removeZone = async (z) => {
    if (!window.confirm(`${t("delete")} "${z}"?`)) return;
    try { const { data } = await api.put(`/restaurants/${rid}/zones`, { zones: zones.filter((x) => x !== z) }); setZones(data.zones); setZone(data.zones[0]); reloadRestaurants(); } catch (ex) { toast.error(errMsg(ex)); }
  };

  const load = useCallback(() => api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data)), [rid]);
  useEffect(() => { load(); }, [load]);
  const live = useRealtime(rid, () => { if (!edit) load(); }, ["orders", "tables", "reservations"]);

  const onDown = (e, tb) => {
    if (!edit) return;
    e.preventDefault();
    const rect = canvas.current.getBoundingClientRect();
    drag.current = { id: tb.id, ox: e.clientX - rect.left - tb.x, oy: e.clientY - rect.top - tb.y };
    setSel(tb.id);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const rect = canvas.current.getBoundingClientRect();
    const x = Math.max(0, snap(e.clientX - rect.left - drag.current.ox));
    const y = Math.max(0, snap(e.clientY - rect.top - drag.current.oy));
    setTables((ts) => ts.map((tb) => (tb.id === drag.current.id ? { ...tb, x, y } : tb)));
    setDirty(true);
  };
  const onUp = () => { drag.current = null; };

  const saveLayout = async () => {
    await api.put(`/restaurants/${rid}/tables/positions`, tables.map(({ id, x, y, w, h }) => ({ id, x, y, w, h })));
    setDirty(false); toast.success(t("saved"));
  };
  const submitForm = async (e) => {
    e.preventDefault();
    const payload = { ...form, seats: +form.seats, w: form.shape === "rect" ? 140 : 90, h: 90 };
    try {
      if (form.id) await api.put(`/restaurants/${rid}/tables/${form.id}`, payload);
      else await api.post(`/restaurants/${rid}/tables`, { ...payload, x: 40, y: 40 });
      setForm(null); load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };
  const del = async (id) => { try { await api.delete(`/restaurants/${rid}/tables/${id}`); setSel(null); load(); } catch (ex) { toast.error(errMsg(ex)); } };
  const setStatus = async (tb, status) => { await api.patch(`/restaurants/${rid}/tables/${tb.id}/status`, { status }); load(); };
  const selected = tables.find((x) => x.id === sel);

  return (
    <div>
      <PageHeader title={t("nav_floor")}>
        <LiveDot connected={live} />
        <Btn data-testid="floor-mode-toggle" variant={edit ? "primary" : "outline"} onClick={() => setEdit(!edit)}><Pencil size={16} />{edit ? t("view_mode") : t("edit_mode")}</Btn>
        {edit && <Btn data-testid="add-table-btn" variant="outline" onClick={() => setForm({ name: `T${tables.length + 1}`, seats: 4, shape: "square", zone })}><Plus size={16} />{t("add_table")}</Btn>}
        {edit && <><input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="bg-file-input" onChange={uploadBg} />
          <Btn data-testid="upload-bg-btn" variant="outline" onClick={() => fileRef.current?.click()}><ImageIcon size={16} />{bgUrl ? t("change_bg") : t("upload_bg")}</Btn>
          {bgUrl && <Btn data-testid="remove-bg-btn" variant="ghost" onClick={removeBg}><ImageOff size={16} /></Btn>}</>}
        {edit && <Btn data-testid="save-layout-btn" disabled={!dirty} onClick={saveLayout}><Save size={16} />{t("save_layout")}</Btn>}
      </PageHeader>
      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="zone-tabs">
        {zones.map((z) => { const n = tables.filter((x) => (x.zone || "Sala") === z); const occ = n.filter((x) => x.order || x.status === "occupied").length; return (
          <button key={z} data-testid={`zone-tab-${z}`} onClick={() => { setZone(z); setSel(null); }} className={`group flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${zone === z ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`}>
            {z}<span className={`rounded-full px-1.5 text-xs ${zone === z ? "bg-white/20" : "bg-slate-100"}`}>{occ}/{n.length}</span>
            {edit && zone === z && <span role="button" data-testid={`zone-rename-${z}`} title={t("rename_zone")} onClick={(e) => { e.stopPropagation(); renameZone(z); }} className="ml-1 opacity-70 hover:opacity-100"><Pencil size={12} /></span>}
            {edit && zones.length > 1 && n.length === 0 && <span role="button" data-testid={`zone-remove-${z}`} onClick={(e) => { e.stopPropagation(); removeZone(z); }} className="ml-1 opacity-60 hover:opacity-100">×</span>}
          </button>); })}
        {edit && <Btn data-testid="add-zone-btn" variant="ghost" className="px-3" disabled={zones.length >= 10} onClick={addZone}><Plus size={14} />{t("new_zone")} <span className="text-xs text-slate-400">({zones.length}/10)</span></Btn>}
        {!edit && <span className="text-xs text-slate-400">{t("zones_hint")}</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs font-medium text-slate-600">
        {["free", "occupied", "reserved", "cleaning"].map((s) => <span key={s} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded-full border-2 ${colors[s].split(" ").slice(0, 2).join(" ")}`} />{t(s)}</span>)}
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_280px]">
        <div ref={canvas} data-testid="floor-canvas" onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          style={bgUrl ? { backgroundImage: `linear-gradient(rgba(255,255,255,0.45), rgba(255,255,255,0.45)), url(${bgUrl})`, backgroundSize: "contain", backgroundRepeat: "no-repeat", backgroundPosition: "center" } : undefined}
          className={`floor-canvas relative min-h-[560px] overflow-hidden rounded-2xl border ${edit ? "border-[#5aa469] ring-2 ring-[#5aa469]/20 cursor-move" : "border-slate-200"}`}>
          {bgUrl && <span data-testid="bg-active" className="absolute bottom-2 right-3 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{t("floorplan_bg")}</span>}
          {zoneTables.map((tb) => {
            const st = tb.order ? "occupied" : tb.status;
            return (
              <div key={tb.id} data-testid={`table-${tb.name}`} onMouseDown={(e) => onDown(e, tb)}
                onClick={() => { if (edit) setSel(tb.id); else setOrderTable(tb); }}
                style={{ left: tb.x, top: tb.y, width: tb.w, height: tb.h }}
                className={`absolute flex select-none flex-col items-center justify-center border-2 shadow-sm transition-shadow hover:shadow-lg ${colors[st]} ${tb.shape === "round" ? "rounded-full" : "rounded-2xl"} ${sel === tb.id && edit ? "ring-4 ring-[#5aa469]/40" : ""} ${!edit ? "cursor-pointer" : ""}`}>
                <span className="font-heading text-lg font-extrabold">{tb.name}</span>
                <span className="text-[11px] opacity-80">{tb.seats} {t("seats")}</span>
                {tb.order && <span className="mt-0.5 rounded-full bg-white/80 px-2 text-[11px] font-bold">{fmt(tb.order.total)} · {elapsed(tb.order.opened_at)}′</span>}
                {tb.order?.has_ready && <span className="absolute -right-1 -top-1 h-4 w-4 animate-pulse rounded-full bg-emerald-500 ring-2 ring-white" />}
                {tb.order?.bill_requested && <span className="absolute -left-1 -top-1 rounded-full bg-sky-500 px-1.5 text-[10px] font-bold text-white">€</span>}
                {tb.order?.call && <span data-testid={`table-call-${tb.name}`} title={tb.order.call === "bill" ? t("call_bill") : t("call_waiter")} className="absolute -bottom-2 left-1/2 -translate-x-1/2 animate-bounce rounded-full bg-amber-500 px-2 text-[10px] font-extrabold text-white shadow">{tb.order.call === "bill" ? "CONTO" : "CHIAMATA"}</span>}
                {tb.order?.paid_online && <span className="absolute -right-1 -bottom-1 rounded-full bg-emerald-600 px-1.5 text-[10px] font-bold text-white">PAGATO</span>}
              </div>
            );
          })}
        </div>
        <div className="space-y-3">
          {edit && selected && (
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="font-heading text-lg font-bold">{selected.name}</p>
              <p className="text-sm text-slate-500">{selected.seats} {t("seats")} · {t(selected.shape)} · {selected.zone || "Sala"}</p>
              <div className="mt-3 flex gap-2">
                <Btn data-testid="edit-table-btn" variant="outline" onClick={() => setForm(selected)}><Pencil size={14} />{t("edit")}</Btn>
                <Btn data-testid="delete-table-btn" variant="danger" onClick={() => del(selected.id)}><Trash2 size={14} />{t("delete")}</Btn>
              </div>
            </div>
          )}
          {!edit && (
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm text-sm text-slate-600">
              <p className="font-heading font-bold text-slate-900">{zone}</p>
              <p className="mt-1">{zoneTables.filter((x) => x.order || x.status === "occupied").length}/{zoneTables.length} {t("occupied").toLowerCase()}</p>
              <ul className="mt-3 space-y-2">
                {zoneTables.filter((x) => x.order).map((x) => <li key={x.id} className="flex justify-between"><span>{x.name}{x.order.waiter && <span data-testid={`table-waiter-${x.name}`} className="ml-1 text-xs text-slate-500">· {x.order.waiter}</span>}</span><span className="font-semibold">{fmt(x.order.total)}</span></li>)}
              </ul>
            </div>
          )}
        </div>
      </div>

      <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? t("edit") : t("add_table")}>
        {form && <form onSubmit={submitForm} className="space-y-3">
          <Field label={t("name")}><input data-testid="table-name-input" className={inp} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label={t("seats")}><input data-testid="table-seats-input" type="number" min="1" className={inp} value={form.seats} onChange={(e) => setForm({ ...form, seats: e.target.value })} /></Field>
          <Field label={t("zone")}><select data-testid="table-zone-select" className={inp} value={form.zone || zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}>{zones.map((z) => <option key={z} value={z}>{z}</option>)}</select></Field>
          <Field label={t("shape")}><select data-testid="table-shape-select" className={inp} value={form.shape} onChange={(e) => setForm({ ...form, shape: e.target.value })}>{["round", "square", "rect"].map((s) => <option key={s} value={s}>{t(s)}</option>)}</select></Field>
          <div className="flex justify-end gap-2 pt-2"><Btn type="button" variant="ghost" onClick={() => setForm(null)}>{t("cancel")}</Btn><Btn data-testid="table-form-submit" type="submit">{t("save")}</Btn></div>
        </form>}
      </Modal>

      <Modal open={!!orderTable} onClose={() => { setOrderTable(null); load(); }} title={`${t("table")} ${orderTable?.name}`} wide>
        {orderTable && <>
          {!orderTable.order && <div className="mb-4 flex gap-2">{["free", "reserved", "cleaning"].map((s) => <Btn key={s} data-testid={`set-status-${s}`} variant={orderTable.status === s ? "primary" : "outline"} onClick={() => { setStatus(orderTable, s); setOrderTable({ ...orderTable, status: s }); }}>{t(s)}</Btn>)}</div>}
          <OrderPanel table={orderTable} onChanged={load} onClose={() => setOrderTable(null)} onPay={(o) => nav(`/app/cashier?order=${o.id}`)} />
        </>}
      </Modal>
    </div>
  );
}
